import { useMutation, useQueryClient } from "react-query";
import { useAgentsHttp } from "../useAgentsHttp";

interface ProjectDeleteResponse {
  project_id: string;
  rows_deleted: number;
  already_absent: boolean;
}

interface ProjectBulkDeleteResponse {
  deleted: { project_id: string; rows_deleted: number }[];
  already_absent: string[];
  failed: { project_id: string; reason: string }[];
}

interface ProjectMergeResponse {
  from_project_id: string;
  to_project_id: string;
  moved: number;
  dropped_meta: number;
}

const invalidateLists = (qc: ReturnType<typeof useQueryClient>) => {
  qc.invalidateQueries({ queryKey: ["inrange-agents-runs-list"] });
  qc.invalidateQueries({ queryKey: ["inrange-agents-projects-list"] });
};

export const useDeleteProject = () => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<ProjectDeleteResponse, Error, string>(
    (project_id) => http<ProjectDeleteResponse>("/projects/delete", { project_id }),
    {
      onSuccess: () => invalidateLists(qc),
    },
  );
};

export const useBulkDeleteProjects = () => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<ProjectBulkDeleteResponse, Error, string[]>(
    (project_ids) =>
      http<ProjectBulkDeleteResponse>("/projects/bulk_delete", {
        project_ids,
      }),
    {
      onSuccess: () => invalidateLists(qc),
    },
  );
};

export const useMergeProjects = () => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<
    ProjectMergeResponse,
    Error,
    { from_project_id: string; to_project_id: string }
  >(
    ({ from_project_id, to_project_id }) =>
      http<ProjectMergeResponse>("/projects/merge", {
        from_project_id,
        to_project_id,
      }),
    {
      onSuccess: () => invalidateLists(qc),
    },
  );
};
